import { numberLocale } from "./format";
/**
 * Agenturfarbe (05_farben.md): Kontrast nach WCAG und funktionale Tonstufen für hell und dunkel.
 * Genutzt von den Einstellungen (Vorschau) und vom Portalrahmen (Agenturfarbe im Portal und im Creator-Bereich).
 */

/**
 * Flächen, auf denen die Agenturfarbe als Schrift steht, wie in portal.css: Karte (--surface), Seite (--bg,
 * --portal-page) und Unterlegung (--subtle, z. B. Avatare). Die erste Fläche ist die Grundlage der zarten Akzentfläche
 * (--accent-soft = 14 % Farbe auf --surface, aktiver Reiter), die je Tonstufe mitgeprüft wird (SICHT-AGENTUR-REST-13).
 */
export const SURFACES = {
  light: ["#FFFFFF", "#F5F7FB", "#F5F7FB", "#F3F4F6"],
  dark: ["#1B1E21", "#131517", "#101214", "#25292D"],
} as const;
/** Anteil der Farbe in --accent-soft (accentCss: color-mix in srgb) */
const SOFT_SHARE = 0.14;
export const ON_ACCENT_DARK = "#14281E";

/** „#abc“, „abc“, „#AABBCC“ → „#AABBCC“; sonst null. */
export function normalizeHex(input: string | null | undefined): string | null {
  const v = (input ?? "").trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{3}$/.test(v)) return `#${v.split("").map((c) => c + c).join("")}`.toUpperCase();
  if (/^[0-9a-fA-F]{6}$/.test(v)) return `#${v}`.toUpperCase();
  return null;
}

export function rgb(hex: string): [number, number, number] {
  const h = normalizeHex(hex);
  if (!h) throw new Error(`Keine Farbe: ${hex}`);
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
}
const toLinear = (c: number) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};
const fromLinear = (l: number) => {
  const c = Math.min(1, Math.max(0, l));
  return Math.round((c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055) * 255);
};
const hex2 = (n: number) => n.toString(16).padStart(2, "0").toUpperCase();

export function luminance(hex: string): number {
  const [r, g, b] = rgb(hex).map(toLinear) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** Kontrastverhältnis nach WCAG (1 bis 21). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a), lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}
export function formatRatio(r: number): string {
  return `${new Intl.NumberFormat(numberLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.floor(r * 100) / 100)}:1`;
}

function toOklch(hex: string): { L: number; C: number; h: number } {
  const [r, g, b] = rgb(hex).map(toLinear) as [number, number, number];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return { L, C: Math.sqrt(A * A + B * B), h: Math.atan2(B, A) };
}
function fromOklch(L: number, C: number, h: number): string {
  const A = C * Math.cos(h), B = C * Math.sin(h);
  const l = Math.pow(L + 0.3963377774 * A + 0.2158037573 * B, 3);
  const m = Math.pow(L - 0.1055613458 * A - 0.0638541728 * B, 3);
  const s = Math.pow(L - 0.0894841775 * A - 1.291485548 * B, 3);
  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const bl = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;
  return `#${hex2(fromLinear(r))}${hex2(fromLinear(g))}${hex2(fromLinear(bl))}`;
}

export interface ToneCheck {
  /** Farbe für Links, Fokus und Knöpfe in diesem Modus */
  color: string;
  /** schwächster Kontrast gegen die Flächen des Modus (samt zarter Akzentfläche) */
  ratio: number;
  /** true: Rohfarbe reichte nicht, eine hellere bzw. dunklere Tonstufe wird genutzt */
  adjusted: boolean;
  /** Schrift auf der Tonstufe (Weiß oder Dunkelgrün, mindestens 4,5:1); null: keine Tonstufe trägt, es bleibt die Plattformfarbe */
  onAccent: string | null;
}
export interface AccentCheck {
  raw: string;
  onAccent: { color: string; ratio: number; ok: boolean };
  light: ToneCheck;
  dark: ToneCheck;
}

/** color-mix(in srgb, farbe anteil, fläche): Mischung der Kanalwerte wie im Browser */
export function mixSrgb(color: string, base: string, share: number): string {
  const a = rgb(color), b = rgb(base);
  return `#${a.map((c, i) => hex2(Math.round(c * share + b[i]! * (1 - share)))).join("")}`;
}
function worst(color: string, surfaces: readonly string[]): number {
  return Math.min(...surfaces.map((s) => contrastRatio(color, s)), contrastRatio(color, mixSrgb(color, surfaces[0]!, SOFT_SHARE)));
}
/** Schrift auf der Farbe: Weiß oder Dunkelgrün, was stärker ist; null, wenn keins 4,5:1 erreicht. */
function onColor(tone: string): string | null {
  const white = contrastRatio(tone, "#FFFFFF");
  const dark = contrastRatio(tone, ON_ACCENT_DARK);
  const best = white >= dark ? { color: "#FFFFFF", ratio: white } : { color: ON_ACCENT_DARK, ratio: dark };
  return best.ratio >= 4.5 ? best.color : null;
}

/**
 * Funktionale Tonstufe nach 05_farben.md: Rohfarbe, wenn sie gegen alle Flächen des Modus (samt zarter Akzentfläche)
 * 4,5:1 erreicht und Schrift auf ihr ebenfalls 4,5:1 erreicht; sonst in OKLCH Buntheit und Farbton halten und die
 * Helligkeit in 0,01-Schritten Richtung Extrem schieben, nach der Rückwandlung neu messen. Ohne Treffer: neutrales
 * Graphit bzw. Weiß. Genau diese Tonstufe setzt accentCss ins Portal (SICHT-AGENTUR-REST-12).
 */
export function functionalTone(raw: string, mode: "light" | "dark"): ToneCheck {
  const surfaces = SURFACES[mode];
  const r0 = worst(raw, surfaces);
  const on0 = onColor(raw);
  if (r0 >= 4.5 && on0) return { color: raw, ratio: r0, adjusted: false, onAccent: on0 };
  const { L, C, h } = toOklch(raw);
  const dir = mode === "light" ? -0.01 : 0.01;
  for (let l = L + dir; l >= 0 && l <= 1; l += dir) {
    const c = fromOklch(l, C, h);
    const r = worst(c, surfaces);
    const on = onColor(c);
    if (r >= 4.5 && on) return { color: c, ratio: r, adjusted: true, onAccent: on };
  }
  const fallback = mode === "light" ? "#202724" : "#F2F5F3";
  const r = worst(fallback, surfaces);
  return { color: fallback, ratio: r, adjusted: true, onAccent: r >= 4.5 ? onColor(fallback) : null };
}

export function checkAccent(input: string): AccentCheck | null {
  const raw = normalizeHex(input);
  if (!raw) return null;
  const white = contrastRatio(raw, "#FFFFFF");
  const dark = contrastRatio(raw, ON_ACCENT_DARK);
  const onAccent = white >= dark ? { color: "#FFFFFF", ratio: white } : { color: ON_ACCENT_DARK, ratio: dark };
  return { raw, onAccent: { ...onAccent, ok: onAccent.ratio >= 4.5 }, light: functionalTone(raw, "light"), dark: functionalTone(raw, "dark") };
}


/**
 * CSS-Variablen für die Agenturfarbe im Portal: je Modus die funktionale Tonstufe, Schrift auf der Farbe mit
 * mindestens 4,5:1. Reicht ein Modus nicht, bleibt dort die Plattformfarbe. null: keine eigene Farbe.
 */
export function accentCss(input: string | null | undefined): string | null {
  const c = input ? checkAccent(input) : null;
  if (!c) return null;
  const vars = (tone: ToneCheck): string | null => {
    if (!tone.onAccent) return null;
    return `--accent:${tone.color};--focus:${tone.color};--on-accent:${tone.onAccent};--accent-soft:color-mix(in srgb, ${tone.color} ${SOFT_SHARE * 100}%, var(--surface));`;
  };
  const light = vars(c.light);
  const dark = vars(c.dark);
  if (!light && !dark) return null;
  return [
    light ? `:root{${light}}` : "",
    // Druck immer mit der hellen Stufe, auch aus dem Dunkelmodus (SICHT-AGENTUR-REST-30; stärker als die Druckregel in app.css)
    light ? `@media print{:root:root:root:root{${light}}}` : "",
    dark ? `[data-theme="dark"]{${dark}}@media(prefers-color-scheme:dark){:root:not([data-theme="light"]){${dark}}}` : "",
  ].join("");
}
